import type { ObligationResolution } from '@statura/utils';

export type ObligationVisibilitySource = 'RESOLVER' | 'LEGACY';

export interface ObligationVisibilityDecision {
  visible: boolean;
  /** Entity-level duties remain visible but cannot be edited by an associated provider. */
  readOnly: boolean;
  /** Plain-language context shown beside an associated-provider duty. */
  label?: 'Via your registered provider';
  source: ObligationVisibilitySource;
  reason: string;
}

/**
 * Resolver-first, legacy-fallback visibility decision.
 *
 * Wave 3 consumers (Quality Standards, the Compliance hub, the audit
 * program) decide whether to show a UI surface from BOTH the pure
 * `@statura/utils` obligation resolver AND today's pre-resolver ("legacy")
 * visibility logic. This is the single place that reconciles the two, so
 * every consumer applies the same rule:
 *
 *  - APPLIES / APPLIES_MODIFIED / VIA_REGISTERED_PROVIDER → visible. Positive
 *    evidence can ADD visibility the legacy axis lacked.
 *  - DOES_NOT_APPLY → hidden. By construction the resolver only returns this
 *    when it has positive evidence (a confirmed profile with a declared,
 *    recognised, non-matching category/entity/programme) — see
 *    packages/utils/src/compliance-obligations/resolver.ts. This is the
 *    only status that can REMOVE visibility the legacy axis had.
 *  - UNKNOWN_SHOW_ANYWAY remains visible: an incomplete provider profile must
 *    never make a universal duty disappear. A missing resolution alone still
 *    falls back to the legacy decision.
 *
 * A caller with no resolver signal for this obligation (resolution is
 * undefined) always gets the legacy result — this covers both "the loader
 * failed" (caller passes undefined for every obligation) and "this
 * obligation id isn't in the catalogue" uniformly, per the design ruling.
 */
export function decideVisibility(
  resolution: ObligationResolution | undefined,
  legacyVisible: boolean,
): ObligationVisibilityDecision {
  if (!resolution) {
    return { visible: legacyVisible, readOnly: false, source: 'LEGACY', reason: 'NO_RESOLUTION' };
  }
  switch (resolution.status) {
    case 'APPLIES':
    case 'APPLIES_MODIFIED':
      return { visible: true, readOnly: false, source: 'RESOLVER', reason: resolution.status };
    case 'VIA_REGISTERED_PROVIDER':
      return {
        visible: true,
        readOnly: true,
        label: 'Via your registered provider',
        source: 'RESOLVER',
        reason: resolution.status,
      };
    case 'DOES_NOT_APPLY':
      return { visible: false, readOnly: false, source: 'RESOLVER', reason: resolution.status };
    case 'UNKNOWN_SHOW_ANYWAY':
    default:
      return { visible: true, readOnly: false, source: 'RESOLVER', reason: 'UNKNOWN_SHOW_ANYWAY' };
  }
}
